"use client";

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { ArrowUpRight, Send, X } from "lucide-react";
import { countryCodes } from "@/components/vitalz-pro/BrochureDownloadButton";
import { contact } from "@/content/contact";
import {
  formatMobileNumber,
  getEmailError,
  getMobileNumberError,
} from "@/lib/formValidation";

type Status = "idle" | "loading" | "success" | "error";

type DemoRequestModalProps = {
  productName: "VitalIQ" | "VitalZ-Pro®";
  variant?: "light" | "dark";
};

const VITALIQ_WEB3FORMS_ACCESS_KEY =
  process.env.NEXT_PUBLIC_WEB3FORMS_VITALIQ_ACCESS_KEY ??
  "b21d59c4-2ffb-4a89-a9a5-bf3b70b39dbc";
const VITALZ_PRO_WEB3FORMS_ACCESS_KEY =
  process.env.NEXT_PUBLIC_WEB3FORMS_VITALZ_PRO_ACCESS_KEY ??
  "20d78f8d-ab22-409f-b547-17e80cead412";
export function DemoRequestModal({
  productName,
  variant = "light",
}: DemoRequestModalProps) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phoneNumber, setPhoneNumber] = useState("");
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const isVitalIQ = productName === "VitalIQ";
  const web3FormsAccessKey = isVitalIQ
    ? VITALIQ_WEB3FORMS_ACCESS_KEY
    : VITALZ_PRO_WEB3FORMS_ACCESS_KEY;

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    nameInputRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), iframe, [href]',
        ),
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [open]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    const fullName = String(formData.get("fullName") || "");
    const email = String(formData.get("email") || "");
    const botcheck = String(formData.get("botcheck") || "");

    const nextEmailError = getEmailError(email);
    if (nextEmailError) {
      setEmailError(nextEmailError);
      return;
    }

    const mobileNumberError = getMobileNumberError(countryCode, phoneNumber);
    if (mobileNumberError) {
      setPhoneError(mobileNumberError);
      return;
    }

    setStatus("loading");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: web3FormsAccessKey,
          subject: `New ${productName} demo request from HealthVoitho.com`,
          from_name: "HealthVoitho Website",
          name: fullName,
          email,
          contact_number: formatMobileNumber(countryCode, phoneNumber),
          product: productName,
          message: `Please contact me to arrange a ${productName} demo.`,
          botcheck,
        }),
      });
      const data = (await response.json()) as {
        success?: boolean;
        message?: string;
      };

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Something went wrong.");
      }

      form.reset();
      setCountryCode("+91");
      setPhoneNumber("");
      setEmailError("");
      setPhoneError("");
      setStatus("success");
    } catch (submissionError) {
      setStatus("error");
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : "Something went wrong.",
      );
    }
  };

  const triggerClassName =
    variant === "light"
      ? "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/45 px-6 py-3.5 text-sm font-semibold text-white outline-none transition hover:border-white/75 hover:bg-white/8 focus-visible:ring-2 focus-visible:ring-[#c3cf95] focus-visible:ring-offset-2 focus-visible:ring-offset-[#102e3a] sm:text-base"
      : "hero-outline-action inline-flex items-center justify-center gap-2 rounded-full border-[1.5px] border-transparent px-6 py-3.5 text-sm font-semibold sm:text-base";

  return (
    <>
      <button
        type="button"
        className={triggerClassName}
        onClick={() => {
          setStatus("idle");
          setError("");
          setEmailError("");
          setPhoneError("");
          setCountryCode("+91");
          setPhoneNumber("");
          setOpen(true);
        }}
      >
        Request a demo
        <ArrowUpRight className="h-4 w-4" aria-hidden />
      </button>

      {open
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-[#071f2d]/78 px-3 py-4 backdrop-blur-sm"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) setOpen(false);
              }}
            >
              <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="relative max-h-[calc(100dvh-1.5rem)] w-full max-w-sm overflow-y-auto bg-white p-4 shadow-[0_28px_90px_rgba(3,20,29,0.34)] sm:p-5"
              >
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-[#42565b] outline-none focus-visible:ring-2 focus-visible:ring-[#468397]"
                  aria-label="Close demo request form"
                >
                  <X className="h-5 w-5" aria-hidden />
                </button>

                {isVitalIQ ? (
                  <Image
                    src="/media/VitalIQ_Logo_HighRes_Transparent.png"
                    alt="VitalIQ"
                    width={3030}
                    height={1260}
                    className="h-auto w-[7.5rem]"
                  />
                ) : (
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2f7f80]">
                    {productName}
                  </p>
                )}
                <h2
                  id={titleId}
                  className="mt-1.5 pr-9 font-display text-2xl leading-tight text-[#1d315f]"
                >
                  Request a demo
                </h2>

                {status === "success" ? (
                  <div className="mt-4 border-t border-[#d7e1df] pt-4" aria-live="polite">
                    <p className="text-sm leading-6 text-[#42565b]">
                      {contact.success}
                    </p>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-[#102e3a] px-6 py-2.5 text-sm font-semibold text-white outline-none focus-visible:ring-2 focus-visible:ring-[#468397] focus-visible:ring-offset-2"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
                    <input
                      type="checkbox"
                      name="botcheck"
                      className="hidden"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                    <label className="block">
                      <span className="text-sm font-semibold text-[#263f45]">
                        Full name
                      </span>
                      <input
                        ref={nameInputRef}
                        type="text"
                        name="fullName"
                        autoComplete="name"
                        placeholder="Enter your full name"
                        required
                        className="mt-1 h-9 w-full border border-[#bdcecb] bg-white px-3 text-sm text-[#1a2426] outline-none focus:border-[#468397] focus:ring-2 focus:ring-[#468397]/20"
                      />
                    </label>

                    <label className="block">
                      <span className="text-sm font-semibold text-[#263f45]">
                        Email address
                      </span>
                      <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        placeholder="Enter your email address"
                        required
                        aria-invalid={Boolean(emailError)}
                        aria-describedby={
                          emailError ? `${titleId}-email-error` : undefined
                        }
                        onBlur={(event) =>
                          setEmailError(getEmailError(event.target.value))
                        }
                        onChange={() => {
                          if (emailError) setEmailError("");
                        }}
                        className="mt-1 h-9 w-full border border-[#bdcecb] bg-white px-3 text-sm text-[#1a2426] outline-none focus:border-[#468397] focus:ring-2 focus:ring-[#468397]/20"
                      />
                      {emailError ? (
                        <span
                          id={`${titleId}-email-error`}
                          className="mt-1.5 block text-xs text-red-700"
                          role="alert"
                        >
                          {emailError}
                        </span>
                      ) : null}
                    </label>

                    <fieldset>
                      <legend className="text-sm font-semibold text-[#263f45]">
                        Contact number
                      </legend>
                      <div className="mt-1 grid grid-cols-[7.25rem_1fr] gap-2">
                        <label className="sr-only" htmlFor={`${titleId}-country-code`}>
                          Country code
                        </label>
                        <select
                          id={`${titleId}-country-code`}
                          name="countryCode"
                          value={countryCode}
                          required
                          onChange={(event) => {
                            const nextCountryCode = event.target.value;
                            setCountryCode(nextCountryCode);
                            setPhoneError(
                              getMobileNumberError(nextCountryCode, phoneNumber),
                            );
                          }}
                          className="h-9 min-w-0 border border-[#bdcecb] bg-white px-2 text-xs text-[#1a2426] outline-none focus:border-[#468397] focus:ring-2 focus:ring-[#468397]/20"
                        >
                          {countryCodes.map((code) => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                        </select>
                        <label className="sr-only" htmlFor={`${titleId}-phone-number`}>
                          Mobile number
                        </label>
                        <input
                          id={`${titleId}-phone-number`}
                          type="tel"
                          name="phoneNumber"
                          autoComplete="tel-national"
                          inputMode="numeric"
                          required
                          pattern="[0-9]+"
                          title="Enter numbers only"
                          placeholder="Enter your mobile number"
                          value={phoneNumber}
                          aria-invalid={Boolean(phoneError)}
                          aria-describedby={
                            phoneError ? `${titleId}-phone-error` : undefined
                          }
                          onBlur={() =>
                            setPhoneError(
                              getMobileNumberError(countryCode, phoneNumber),
                            )
                          }
                          onChange={(event) => {
                            setPhoneNumber(
                              event.target.value.replace(/\D/g, "").slice(0, 15),
                            );
                            if (phoneError) setPhoneError("");
                          }}
                          className="h-9 min-w-0 border border-[#bdcecb] bg-white px-3 text-sm text-[#1a2426] outline-none focus:border-[#468397] focus:ring-2 focus:ring-[#468397]/20"
                        />
                      </div>
                      {phoneError ? (
                        <p
                          id={`${titleId}-phone-error`}
                          className="mt-1.5 text-xs text-red-700"
                          role="alert"
                        >
                          {phoneError}
                        </p>
                      ) : null}
                    </fieldset>

                    <label className="flex items-start gap-2.5 border-t border-[#d7e1df] pt-3 text-xs leading-[1.15rem] text-[#42565b]">
                      <input
                        type="checkbox"
                        name="contactConsent"
                        defaultChecked
                        required
                        className="mt-1 h-4 w-4 shrink-0 accent-[#2f7f80]"
                      />
                      <span>
                        By submitting this form, you confirm that HealthVoitho may
                        contact you using the details provided.
                      </span>
                    </label>

                    {error ? (
                      <p className="text-xs text-red-700" role="alert">
                        {error}
                      </p>
                    ) : null}

                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#102e3a] px-6 py-2.5 text-sm font-semibold text-white outline-none focus-visible:ring-2 focus-visible:ring-[#468397] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60"
                    >
                      {status === "loading" ? "Sending…" : "Request demo"}
                      <Send className="h-4 w-4" aria-hidden />
                    </button>
                  </form>
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
